import { ingredientById } from "../data/ingredients";
import { recipeById } from "../data/recipes";
import {
  getCandidates,
  getCandidatesFromRecipes,
  withIngredient,
} from "./candidates";
import { localizedIngredientName } from "./localize";
import { pluralize } from "./pluralize";
import {
  COOKING_LOCATIONS,
  FOOD_TYPES,
  type Candidate,
  type CulinaryRole,
  type Locale,
  type RecipeFamily,
  type SelectionState,
  type Suggestion,
} from "./types";

export interface RecommendationSource {
  recipeById: ReadonlyMap<string, RecipeFamily>;
  getCandidates: (selection: SelectionState) => Candidate[];
}

const productionSource: RecommendationSource = {
  recipeById,
  getCandidates,
};

export function compatibleIngredientIds(
  selection: SelectionState,
  candidates: Candidate[],
  source: RecommendationSource = productionSource,
): Set<string> {
  const basePool =
    selection.ingredientIds.length > 0
      ? source.getCandidates({ ...selection, ingredientIds: [] })
      : candidates;

  const ids = new Set<string>();
  const selectedSet = new Set(selection.ingredientIds);

  for (const candidate of basePool) {
    const recipe = source.recipeById.get(candidate.recipeId);
    if (!recipe) continue;
    for (const slot of recipe.slots) {
      for (const option of slot.options) {
        const id = option.ingredientId;
        if (selectedSet.has(id) || ids.has(id)) continue;
        const ingredient = ingredientById.get(id);
        if (!ingredient || ingredient.assumedStaple) continue;
        if (!selection.veganOnly || ingredient.dietTags.includes("vegan")) {
          ids.add(id);
        }
      }
    }
  }
  return ids;
}

function selectedRoles(selection: SelectionState): Set<CulinaryRole> {
  return new Set(
    selection.ingredientIds.flatMap(
      (ingredientId) => ingredientById.get(ingredientId)?.roles ?? [],
    ),
  );
}

function ingredientSuggestions(
  selection: SelectionState,
  candidates: Candidate[],
  source: RecommendationSource,
  locale: Locale,
): Suggestion[] {
  const roles = selectedRoles(selection);
  const selectedSet = new Set(selection.ingredientIds);
  const potentialIds = new Set<string>();

  for (const candidate of candidates) {
    const recipe = source.recipeById.get(candidate.recipeId);
    if (!recipe) continue;
    for (const slot of recipe.slots) {
      for (const option of slot.options) {
        if (selectedSet.has(option.ingredientId)) continue;
        const ingredient = ingredientById.get(option.ingredientId);
        if (!ingredient || ingredient.assumedStaple) continue;
        potentialIds.add(option.ingredientId);
      }
    }
  }

  // Recipes that don't contain a given potential ingredient can never change
  // membership or assignment when that ingredient is added to the selection
  // (matchSelectedIngredients can only assign an ingredient to a slot that
  // offers it), so their existing candidate can be reused unchanged. Only
  // recipes that actually offer the ingredient need re-matching, which lets
  // us avoid rescanning the entire catalog once per potential ingredient.
  const recipesByPotentialId = new Map<string, Map<string, RecipeFamily>>();
  for (const recipe of source.recipeById.values()) {
    for (const slot of recipe.slots) {
      for (const option of slot.options) {
        if (!potentialIds.has(option.ingredientId)) continue;
        let recipes = recipesByPotentialId.get(option.ingredientId);
        if (!recipes) {
          recipes = new Map();
          recipesByPotentialId.set(option.ingredientId, recipes);
        }
        recipes.set(recipe.id, recipe);
      }
    }
  }

  const ranked = [...potentialIds]
    .flatMap((id) => {
      const ingredient = ingredientById.get(id);
      if (!ingredient) return [];
      const containingRecipes = [
        ...(recipesByPotentialId.get(id)?.values() ?? []),
      ];
      const containingRecipeIds = new Set(
        containingRecipes.map((recipe) => recipe.id),
      );
      // When no ingredient is selected yet, matchSelectedIngredients takes a
      // trivial "match everything" path; adding the first ingredient switches
      // it onto the real backtracking path, which only matches recipes that
      // actually offer that ingredient. So recipes that don't contain `id`
      // can only be reused unchanged if the base selection already had at
      // least one ingredient (i.e. was already on the backtracking path).
      const supportedCandidates =
        selection.ingredientIds.length === 0
          ? getCandidatesFromRecipes(
              withIngredient(selection, id),
              containingRecipes,
            )
          : [
              ...getCandidatesFromRecipes(
                withIngredient(selection, id),
                containingRecipes,
              ),
              ...candidates.filter(
                (candidate) => !containingRecipeIds.has(candidate.recipeId),
              ),
            ];
      if (supportedCandidates.length === 0) return [];
      const canonical = supportedCandidates.filter((candidate) => {
        const recipe = source.recipeById.get(candidate.recipeId);
        return Object.entries(candidate.slotAssignment).some(
          ([slotId, ingredientId]) =>
            ingredientId === id &&
            recipe?.slots.find((slot) => slot.id === slotId)
              ?.canonicalIngredientId === id,
        );
      }).length;
      const missingRole = ingredient.roles.some(
        (role) =>
          !roles.has(role) &&
          supportedCandidates.some((candidate) =>
            source.recipeById
              .get(candidate.recipeId)
              ?.targetBalanceRoles.includes(role),
          ),
      );
      return [
        {
          id,
          ingredient,
          score: {
            support: supportedCandidates.length,
            canonical,
            missingRole,
          },
        },
      ];
    })
    .sort(
      (a, b) =>
        Number(b.score.missingRole) - Number(a.score.missingRole) ||
        b.score.support - a.score.support ||
        b.score.canonical - a.score.canonical ||
        localizedIngredientName(a.ingredient, locale).localeCompare(
          localizedIngredientName(b.ingredient, locale),
        ),
    );

  const usedCategories = new Set<string>();
  const suggestions: Suggestion[] = [];
  for (const item of ranked) {
    if (usedCategories.has(item.ingredient.category)) continue;
    usedCategories.add(item.ingredient.category);
    const role = item.ingredient.roles.find(
      (candidateRole) => !roles.has(candidateRole),
    );
    const support = item.score.support;
    suggestions.push({
      kind: "ingredient",
      id: item.id,
      label: localizedIngredientName(item.ingredient, locale),
      category: item.ingredient.category,
      reason: role
        ? `Adds ${role} and keeps ${support} ${pluralize(support, locale, { en: ["recipe", "recipes"] })} in play.`
        : `Works with ${support} ${pluralize(support, locale, { en: ["recipe", "recipes"] })} here.`,
    });
    if (suggestions.length === 4) break;
  }
  return suggestions;
}

export function getSuggestions(
  selection: SelectionState,
  candidates = getCandidates(selection),
  source: RecommendationSource = productionSource,
  locale: Locale = "en",
): Suggestion[] {
  const suggestions: Suggestion[] = [];

  if (selection.foodTypeIds.length === 0) {
    const counts = new Map<string, number>();
    for (const candidate of candidates) {
      const id = source.recipeById.get(candidate.recipeId)?.foodType;
      if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    const option = FOOD_TYPES.filter((item) => counts.has(item.id)).sort(
      (a, b) =>
        (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0) ||
        a.label.localeCompare(b.label),
    )[0];
    if (option) {
      suggestions.push({
        kind: "food-type",
        id: option.id,
        label: option.label,
        reason: `${counts.get(option.id)} matching ideas take this shape.`,
      });
    }
  }

  if (selection.cookingLocationIds.length === 0) {
    const counts = new Map<string, number>();
    for (const candidate of candidates) {
      const id = source.recipeById.get(
        candidate.recipeId,
      )?.primaryCookingLocation;
      if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    const option = COOKING_LOCATIONS.filter((item) => counts.has(item.id)).sort(
      (a, b) =>
        (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0) ||
        a.label.localeCompare(b.label),
    )[0];
    if (option) {
      suggestions.push({
        kind: "cooking-location",
        id: option.id,
        label: option.label,
        reason: `${counts.get(option.id)} matching ideas use this setup.`,
      });
    }
  }

  suggestions.push(
    ...ingredientSuggestions(selection, candidates, source, locale),
  );
  return suggestions.slice(0, 6);
}
